import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Briefcase, MapPin, Clock, Send } from "lucide-react";
import { SectionHeading, SectionLabel } from "../components/SectionHeading";
import api from "../lib/api";
import { useSite } from "../context/SiteContext";

export default function CareersPage() {
  const { positions } = useSite();
  const [selected, setSelected] = useState("");
  const [form, setForm] = useState({
    name: "", email: "", phone: "", experience: "", cover_letter: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!selected) {
      toast.error("Please select a position to apply for.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/careers/apply", {
        name: form.name,
        email: form.email,
        phone: form.phone || null,
        position: selected,
        experience: form.experience || null,
        cover_letter: form.cover_letter || null,
      });
      toast.success("Application submitted. Our HR team will reach out.");
      setForm({ name: "", email: "", phone: "", experience: "", cover_letter: "" });
      setSelected("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit application.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="careers-page">
      <section className="border-b border-[#1F3550] relative">
        <div className="absolute inset-0 blueprint-grid opacity-30" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-24 md:py-32">
          <SectionLabel code="K/00">Careers</SectionLabel>
          <h1 className="mt-6 font-heading font-black text-4xl md:text-6xl uppercase tracking-tight leading-[0.95] max-w-4xl">
            Build with the <span className="text-[#F39C12]">best in heavy industry</span>.
          </h1>
          <p className="mt-6 text-lg text-[#94A3B8] max-w-3xl leading-relaxed">
            We hire engineers, fabricators, supervisors and safety professionals who
            take ownership of outcomes. Join a team that delivers what it commits.
          </p>
        </div>
      </section>

      {/* Positions */}
      <section className="border-b border-[#1F3550]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20">
          <SectionHeading code="K/01" overline="Open Positions" title="Where you can join us." />
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#1F3550] border border-[#1F3550]">
            {positions.map((p) => (
              <div
                key={p.id}
                data-testid={`position-${p.id}`}
                className={`bg-[#050A0F] p-7 hover:bg-[#0D1B2A] transition-colors cursor-pointer ${selected === p.title ? "outline outline-2 outline-[#F39C12]" : ""}`}
                onClick={() => {
                  setSelected(p.title);
                  document.getElementById("apply-form")?.scrollIntoView({ behavior: "smooth" });
                }}
              >
                <Briefcase className="h-6 w-6 text-[#F39C12]" />
                <h3 className="mt-5 font-heading font-bold text-lg text-[#F8FAFC]">{p.title}</h3>
                <div className="mt-4 space-y-2 font-mono text-[10px] tracking-[0.25em] uppercase text-[#94A3B8]">
                  <div className="flex items-center gap-2"><MapPin className="h-3 w-3 text-[#F39C12]" />{p.location}</div>
                  <div className="flex items-center gap-2"><Clock className="h-3 w-3 text-[#F39C12]" />{p.type} · {p.experience}</div>
                </div>
                <button
                  data-testid={`apply-btn-${p.id}`}
                  className="mt-6 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#F39C12]"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelected(p.title);
                    document.getElementById("apply-form")?.scrollIntoView({ behavior: "smooth" });
                  }}
                >
                  Apply Now →
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Application form */}
      <section id="apply-form" className="border-b border-[#1F3550]">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 py-20 md:py-28">
          <SectionHeading code="K/02" overline="Apply Now" title="Send us your details." />
          <form onSubmit={onSubmit} data-testid="career-form" className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="md:col-span-2">
              <label className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#94A3B8]">Position *</label>
              <select
                data-testid="career-position"
                value={selected}
                onChange={(e) => setSelected(e.target.value)}
                required
                className="mt-2 w-full bg-[#050A0F] border border-[#1F3550] text-[#F8FAFC] focus:border-[#F39C12] focus:ring-1 focus:ring-[#F39C12] px-4 py-3 outline-none"
              >
                <option value="">-- Select Position --</option>
                {positions.map((p) => <option key={p.id} value={p.title}>{p.title}</option>)}
              </select>
            </div>
            <Input testid="career-name" label="Full Name *" name="name" value={form.name} onChange={onChange} required />
            <Input testid="career-email" label="Email *" name="email" type="email" value={form.email} onChange={onChange} required />
            <Input testid="career-phone" label="Phone" name="phone" value={form.phone} onChange={onChange} />
            <Input testid="career-experience" label="Years of Experience" name="experience" value={form.experience} onChange={onChange} />
            <div className="md:col-span-2">
              <label className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#94A3B8]">Cover Letter</label>
              <textarea
                data-testid="career-cover"
                name="cover_letter"
                value={form.cover_letter}
                onChange={onChange}
                rows={5}
                placeholder="Tell us about your relevant projects..."
                className="mt-2 w-full bg-[#050A0F] border border-[#1F3550] text-[#F8FAFC] focus:border-[#F39C12] focus:ring-1 focus:ring-[#F39C12] px-4 py-3 outline-none placeholder:text-[#64748B]"
              />
            </div>
            <div className="md:col-span-2">
              <button
                type="submit"
                data-testid="career-submit"
                disabled={submitting}
                className="inline-flex items-center gap-2 bg-[#F39C12] text-[#050A0F] px-8 py-4 font-bold uppercase tracking-widest text-sm hover:bg-[#D68910] transition-colors disabled:opacity-50"
              >
                {submitting ? "Submitting..." : "Submit Application"} <Send className="h-4 w-4" />
              </button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}

function Input({ label, testid, ...props }) {
  return (
    <div>
      <label className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#94A3B8]">{label}</label>
      <input
        data-testid={testid}
        {...props}
        className="mt-2 w-full bg-[#050A0F] border border-[#1F3550] text-[#F8FAFC] focus:border-[#F39C12] focus:ring-1 focus:ring-[#F39C12] px-4 py-3 outline-none placeholder:text-[#64748B]"
      />
    </div>
  );
}
